import React from 'react';
import { Spinner, SpinnerSize, Text } from '@fluentui/react';
import { VerticalBarChart, IVerticalBarChartDataPoint, LineChart, IChartProps, DonutChart, IChartDataPoint, ResponsiveContainer } from '@fluentui/react-charting';
import { IDashboardWidget, IReportingWidget, IWidgetConfiguration, IWidgetDataResponse, getWidgetDisplayName } from '../../models/Dashboard';
import { useGetWidgetDataQuery } from '../../store/endpoints/DashboardEndpoints';

interface IDashboardWidgetWrapperProps {
    widget: IDashboardWidget;
    widgetMeta?: IReportingWidget;
    editMode?: boolean;
    onRemove?: () => void;
    onConfigure?: () => void;
    resource: string;
    resourceId?: string;
}

const DashboardWidgetWrapper: React.FC<IDashboardWidgetWrapperProps> = ({ widget, widgetMeta, editMode, onRemove, onConfigure, resource, resourceId: resourceId }) => {
    const widgetTitle = getWidgetDisplayName(widget.widgetType);
    const configSummary = getConfigSummary(widget.configuration);

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #edebe9',
            borderRadius: '4px',
            background: '#fff',
            boxShadow: editMode ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
            overflow: 'hidden',
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5em 0.5em',
                borderBottom: '1px solid #edebe9',
                background: '#faf9f8',
                cursor: editMode ? 'move' : 'default',
            }} className="widget-drag-handle">
                <Text variant="smallPlus" style={{ fontWeight: 600 }}>{widgetTitle}</Text>
                <div
                    style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
                    className="widget-no-drag"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                >
                    {configSummary && <Text variant="tiny" style={{ color: '#605e5c' }}>{configSummary}</Text>}
                    {editMode && onConfigure && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onConfigure(); }}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px' }}
                            title="Configure widget"
                        >⚙️</button>
                    )}
                    {editMode && onRemove && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px', color: '#a4262c' }}
                            title="Remove widget"
                        >✕</button>
                    )}
                </div>
            </div>
            <div style={{ flex: 1, padding: '12px', overflow: 'auto' }}>
                <WidgetContent widget={widget} widgetMeta={widgetMeta} resource={resource} storefrontId={resourceId} />
            </div>
        </div>
    );
};

const WidgetContent: React.FC<{ widget: IDashboardWidget; widgetMeta?: IReportingWidget; resource: string; storefrontId?: string }> = ({ widget, widgetMeta, resource, storefrontId }) => {
    const config = widget.configuration || {};
    const { data, isLoading, isError } = useGetWidgetDataQuery({
        resource,
        widgetType: widget.widgetType || '',
        timeframe: config.timeframe,
        timeframeValue: config.timeframeValue,
        interval: config.interval,
        metric: config.metric,
        resourceFilter: config.resourceFilter ?? storefrontId,
        filter: config.filter,
    });

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Spinner size={SpinnerSize.medium} label="Loading..." />
            </div>
        );
    }

    if (isError) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a4262c' }}>
                <Text variant="small">Failed to load data</Text>
            </div>
        );
    }

    const metric = config.metric;
    const isCurrencyMetric = metric === 'Revenue' || metric === 'Profits';
    const displayType = widgetMeta?.displayType;

    switch (displayType) {
        case 'BarChart':
            return <BarChartWidget data={data} isCurrency={isCurrencyMetric} interval={config.interval} />;
        case 'LineChart':
            return <LineChartWidget data={data} isCurrency={isCurrencyMetric} interval={config.interval} />;
        case 'PieChart':
            return <PieChartWidget data={data} isCurrency={isCurrencyMetric} />;
        case 'Metric':
            return <StatWidget
                value={formatMetricValue(data, metric)}
                subtitle={metric ?? getTimeframeLabel(config)}
            />;
        case 'Table':
            return <TableWidget data={data} />;
        default:
            return <Text>Unknown widget display type</Text>;
    }
};

const CHART_COLORS = ['#0078d4', '#2b88d8', '#71afe5', '#005a9e', '#004578', '#106ebe', '#005a9e'];

const BarChartWidget: React.FC<{ data?: IWidgetDataResponse; isCurrency?: boolean, interval?: string }> = ({ data, isCurrency, interval }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = React.useState({ width: 300, height: 200 });
    const points = data?.dataPoints || [];

    React.useEffect(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width: Math.max(width, 100), height: Math.max(height, 100) });
        }
    }, []);

    if (points.length === 0) {
        return <EmptyState message="No data for this period" />;
    }

    const chartData: IVerticalBarChartDataPoint[] = points.map((p, i) => ({
        x: formatLabel(p.label, interval),
        y: p.value,
        color: CHART_COLORS[i % CHART_COLORS.length],
    }));

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <ResponsiveContainer>
                <VerticalBarChart
                    data={chartData}
                    width={dimensions.width}
                    height={dimensions.height}
                    yAxisTickFormat={isCurrency ? (val: number | string) => formatCompactCurrency(val as number) : undefined}
                    barWidth={Math.min(24, Math.max(8, (dimensions.width - 60) / points.length - 4))}
                    hideLegend
                    hideTooltip={true}
                />
            </ResponsiveContainer>
        </div>
    );
};

const LineChartWidget: React.FC<{ data?: IWidgetDataResponse; isCurrency?: boolean, interval?: string }> = ({ data, isCurrency, interval }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = React.useState({ width: 300, height: 200 });
    const points = data?.dataPoints || [];

    React.useEffect(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width: Math.max(width, 100), height: Math.max(height, 100) });
        }
    }, []);

    if (points.length === 0) {
        return <EmptyState message="No data for this period" />;
    }

    const chartProps: IChartProps = {
        chartTitle: '',
        lineChartData: [{
            legend: '',
            data: points.map((p, i) => ({
                x: new Date(p.label ?? ''),
                y: p.value,
                xAxisCalloutData: formatLabel(p.label, interval),
            })),
            color: CHART_COLORS[0],
        }],
    };

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <ResponsiveContainer>
                <LineChart
                    data={chartProps}
                    width={dimensions.width}
                    height={dimensions.height}
                    yAxisTickFormat={isCurrency ? (val: number | string) => formatCompactCurrency(val as number) : undefined}
                    hideLegend
                />
            </ResponsiveContainer>
        </div>
    );
};

const PieChartWidget: React.FC<{ data?: IWidgetDataResponse; isCurrency?: boolean }> = ({ data, isCurrency }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = React.useState({ width: 250, height: 200 });
    const points = data?.dataPoints || [];

    React.useEffect(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width: Math.max(width, 100), height: Math.max(height, 100) });
        }
    }, []);

    if (points.length === 0) {
        return <EmptyState message="No data for this period" />;
    }

    const total = points.reduce((sum, p) => sum + p.value, 0);
    const chartData: IChartDataPoint[] = points.slice(0, 8).map((p, i) => ({
        legend: p.label,
        data: p.value,
        color: CHART_COLORS[i % CHART_COLORS.length],
        yAxisCalloutData: isCurrency ? formatCurrency(p.value) : formatNumber(p.value),
    }));

    const size = Math.min(dimensions.width, dimensions.height);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer>
                <DonutChart
                    data={{ chartData }}
                    innerRadius={Math.max(size * 0.15, 30)}
                    height={size}
                    width={dimensions.width}
                    hideLabels={false}
                    valueInsideDonut={isCurrency ? formatCompactCurrency(total) : formatCompactNumber(total)}
                    hideLegend={true}
                />
            </ResponsiveContainer>
        </div>
    );
};

const StatWidget: React.FC<{ value: string; subtitle?: string }> = ({ value, subtitle }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Text variant="xxLarge" style={{ fontWeight: 700, margin: '8px 0' }}>{value}</Text>
        {subtitle && <Text variant="tiny" style={{ color: '#a19f9d' }}>{subtitle}</Text>}
    </div>
);

const TableWidget: React.FC<{ data?: IWidgetDataResponse }> = ({ data }) => {
    const rows = data?.rows || [];
    // Derive column headers from the union of all row keys, preserving first-seen order.
    const columns: string[] = [];
    const seen = new Set<string>();
    rows.forEach(r => {
        if (r.data) {
            Object.keys(r.data).forEach(k => {
                if (!seen.has(k)) {
                    seen.add(k);
                    columns.push(k);
                }
            });
        }
    });
    const showDate = rows.some(r => !!r.date);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #edebe9' }}>
                        {columns.map(c => (
                            <th key={c} style={{ textAlign: 'left', padding: '4px 8px' }}>{c}</th>
                        ))}
                        {showDate && <th style={{ textAlign: 'left', padding: '4px 8px' }}>Date</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={columns.length + (showDate ? 1 : 0)} style={{ textAlign: 'center', padding: '24px', color: '#a19f9d' }}>No data available</td></tr>
                    ) : (
                        rows.map((row, i) => (
                            <tr key={row.id ?? i} style={{ borderBottom: '1px solid #f3f2f1' }}>
                                {columns.map(c => (
                                    <td key={c} style={{ padding: '4px 8px' }}>{row.data?.[c] ?? '-'}</td>
                                ))}
                                {showDate && <td style={{ padding: '4px 8px' }}>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</td>}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a19f9d' }}>
        <Text variant="small">{message}</Text>
    </div>
);

function formatMetricValue(data: IWidgetDataResponse | undefined, metric?: string): string {
    if (!data) return '0';
    if (metric === 'Orders') {
        return formatNumber(data.totalCount ?? data.totalValue ?? 0);
    }
    if (metric === 'Quantity') {
        return formatNumber(data.totalValue ?? data.totalCount ?? 0);
    }
    if (metric === 'Revenue' || metric === 'Profits') {
        return formatCurrency(data.totalValue ?? 0);
    }
    // No metric (e.g. ActiveUsers) — prefer totalCount.
    return formatNumber(data.totalCount ?? data.totalValue ?? 0);
}

function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
}

function formatCompactCurrency(value: number): string {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
}

function formatCompactNumber(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return value.toFixed(0);
}

function formatLabel(label?: string, interval?: string): string {
    if (!label) return '';
    const date = new Date(label);
    if (!isNaN(date.getTime())) {
        const normalized = interval?.toLowerCase();
        if (normalized === 'month' || normalized === 'quarter') {
            return `${date.getMonth() + 1}/${date.getFullYear()}`;
        }
        if (normalized === 'year') {
            return `${date.getFullYear()}`;
        }
        return `${date.getMonth() + 1}/${date.getDate()}`;
    }
    return label.length > 12 ? label.substring(0, 12) : label;
}

function getConfigSummary(config?: IWidgetConfiguration): string | null {
    if (!config) return null;
    if (config.timeframe === 'ytd') return 'YTD';
    if (config.timeframe === 'mtd') return 'MTD';
    if (config.timeframe === 'lastMonth') return 'Last Month';
    if (config.timeframe === 'days' && config.timeframeValue) return `Last ${config.timeframeValue} days`;
    if (config.timeframe === 'months' && config.timeframeValue) return `Last ${config.timeframeValue} months`;
    if (config.timeframe === 'years' && config.timeframeValue) return `Last ${config.timeframeValue} years`;
    return null;
}

function getTimeframeLabel(config?: IWidgetConfiguration): string {
    if (!config) return 'Last 30 days';
    if (config.timeframe === 'ytd') return 'Year to Date';
    if (config.timeframe === 'mtd') return 'Month to Date';
    if (config.timeframe === 'lastMonth') return 'Last Month';
    if (config.timeframe === 'days') return `Last ${config.timeframeValue || 30} days`;
    if (config.timeframe === 'months') return `Last ${config.timeframeValue || 12} months`;
    if (config.timeframe === 'years') return `Last ${config.timeframeValue || 1} year(s)`;
    return 'Last 30 days';
}

export default DashboardWidgetWrapper;
